import React, { useState, useEffect } from 'react';
import { ReviewService } from '@/lib/reviewService';
import { useAuth } from '@/contexts/AuthContext';
import { ReviewWithUser, ReviewTargetType, RatingStats } from '@/types/reviews';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  Star, 
  ThumbsUp, 
  ThumbsDown, 
  Flag, 
  CheckCircle,
  Calendar,
  TrendingUp,
  TrendingDown,
  Filter
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

interface StarDisplayProps {
  rating: number;
  size?: 'sm' | 'md' | 'lg';
  showNumber?: boolean;
}

const StarDisplay: React.FC<StarDisplayProps> = ({ rating, size = 'md', showNumber = false }) => {
  const sizeClasses = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  };

  return (
    <div className="flex items-center gap-1">
      <div className="flex">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${sizeClasses[size]} ${
              star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
            }`}
          />
        ))}
      </div>
      {showNumber && (
        <span className="text-sm text-gray-600 ml-1">{rating}</span>
      )}
    </div>
  );
};

interface RatingStatsDisplayProps {
  stats: RatingStats;
}

const RatingStatsDisplay: React.FC<RatingStatsDisplayProps> = ({ stats }) => {
  const { average_rating, rating_count, rating_distribution } = stats;

  if (rating_count === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Star className="w-12 h-12 mx-auto mb-2 text-gray-300" />
        <p>No reviews yet</p>
        <p className="text-sm">Be the first to review!</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 p-6 rounded-lg">
      <div className="flex items-center gap-4 mb-4">
        <div className="text-center">
          <div className="text-3xl font-bold">{average_rating.toFixed(1)}</div>
          <StarDisplay rating={Math.round(average_rating)} size="lg" />
          <div className="text-sm text-gray-600 mt-1">
            {rating_count} {rating_count === 1 ? 'review' : 'reviews'}
          </div>
        </div>
        
        <div className="flex-1">
          {[5, 4, 3, 2, 1].map((stars) => {
            const count = rating_distribution[stars] || 0;
            const percentage = rating_count > 0 ? (count / rating_count) * 100 : 0;
            
            return (
              <div key={stars} className="flex items-center gap-2 mb-1">
                <span className="text-sm w-3">{stars}</span>
                <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                <div className="flex-1 bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-yellow-400 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${percentage}%` }}
                  />
                </div>
                <span className="text-sm text-gray-600 w-8">{count}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

interface ReviewCardProps {
  review: ReviewWithUser;
  onVote?: (reviewId: string, voteType: 'helpful' | 'not_helpful' | null) => void;
  onFlag?: (reviewId: string) => void;
  userVote?: 'helpful' | 'not_helpful' | null;
}

const ReviewCard: React.FC<ReviewCardProps> = ({ review, onVote, onFlag, userVote }) => {
  const { user } = useAuth();
  const [isVoting, setIsVoting] = useState(false);

  const handleVote = async (voteType: 'helpful' | 'not_helpful') => {
    if (!user || !onVote || isVoting) return;
    
    setIsVoting(true);
    try {
      // If already voted the same way, remove vote; otherwise update vote
      const newVote = userVote === voteType ? null : voteType;
      await onVote(review.id, newVote);
    } finally {
      setIsVoting(false);
    }
  };

  const getUserDisplayName = () => {
    if (review.user?.first_name || review.user?.last_name) {
      return `${review.user.first_name || ''} ${review.user.last_name || ''}`.trim();
    }
    return 'Anonymous User';
  };

  const getUserInitials = () => {
    const name = getUserDisplayName();
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Card className="mb-4">
      <CardContent className="pt-6">
        <div className="flex gap-4">
          <Avatar className="w-10 h-10">
            <AvatarImage src={review.user?.avatar_url} />
            <AvatarFallback>{getUserInitials()}</AvatarFallback>
          </Avatar>
          
          <div className="flex-1">
            {/* Header */}
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium">{getUserDisplayName()}</span>
                  {review.verified_purchase && (
                    <Badge variant="secondary" className="text-xs">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Verified Purchase
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <StarDisplay rating={review.rating} size="sm" showNumber />
                  {review.created_at && (
                    <>
                      <span>•</span>
                      <Calendar className="w-3 h-3" />
                      <span>{formatDistanceToNow(new Date(review.created_at), { addSuffix: true })}</span>
                    </>
                  )}
                </div>
              </div>
              
              {user && user.id !== review.user_id && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm">
                      <Flag className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem onClick={() => onFlag?.(review.id)}>
                      Report Review
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
            
            {/* Title */}
            {review.title && (
              <h4 className="font-medium mb-2">{review.title}</h4>
            )}
            
            {/* Content */}
            {review.content && (
              <p className="text-gray-700 mb-4 whitespace-pre-wrap">{review.content}</p>
            )}
            
            {/* Category Ratings */}
            {review.category_ratings && Object.keys(review.category_ratings).length > 0 && (
              <div className="mb-4">
                <h5 className="text-sm font-medium mb-2">Category Ratings</h5>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(review.category_ratings).map(([category, rating]) => (
                    <div key={category} className="flex items-center justify-between text-sm">
                      <span className="capitalize">{category.replace('_', ' ')}</span>
                      <StarDisplay rating={rating as number} size="sm" showNumber />
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Recommendation */}
            {review.recommended !== undefined && (
              <div className="mb-4">
                <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                  review.recommended 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-red-100 text-red-700'
                }`}>
                  {review.recommended ? (
                    <>
                      <TrendingUp className="w-3 h-3" />
                      Recommends this
                    </>
                  ) : (
                    <>
                      <TrendingDown className="w-3 h-3" />
                      Doesn't recommend
                    </>
                  )}
                </div>
              </div>
            )}
            
            {/* Actions */}
            {user && user.id !== review.user_id && (
              <div className="flex items-center gap-4 pt-2">
                <span className="text-sm text-gray-600">Was this helpful?</span>
                <div className="flex gap-2">
                  <Button
                    variant={userVote === 'helpful' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleVote('helpful')}
                    disabled={isVoting}
                    className="flex items-center gap-1"
                  >
                    <ThumbsUp className="w-3 h-3" />
                    <span>{review.helpful_votes_count || 0}</span>
                  </Button>
                  <Button
                    variant={userVote === 'not_helpful' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleVote('not_helpful')}
                    disabled={isVoting}
                    className="flex items-center gap-1"
                  >
                    <ThumbsDown className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

interface ReviewDisplayProps {
  targetType: ReviewTargetType;
  targetId: string;
  className?: string;
}

export const ReviewDisplay: React.FC<ReviewDisplayProps> = ({
  targetType,
  targetId,
  className = ''
}) => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState<ReviewWithUser[]>([]);
  const [stats, setStats] = useState<RatingStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'created_at' | 'rating' | 'helpful_votes'>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [userVotes, setUserVotes] = useState<Record<string, 'helpful' | 'not_helpful'>>({});

  // Load reviews and stats
  useEffect(() => {
    loadData();
  }, [targetType, targetId, sortBy, sortOrder]);

  // Load user votes for reviews
  useEffect(() => {
    if (user && reviews.length > 0) {
      loadUserVotes();
    }
  }, [user, reviews.length]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [reviewsData, statsData] = await Promise.all([
        ReviewService.getReviews(targetType, targetId, {
          limit: 50,
          sortBy,
          sortOrder
        }),
        ReviewService.getRatingStats(targetType, targetId)
      ]);
      
      setReviews(reviewsData);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading reviews:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to load reviews';
      setError(errorMessage);
      toast.error(errorMessage);
      
      // Set empty data on error
      setReviews([]);
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  const loadUserVotes = async () => {
    if (!user) return;
    
    try {
      const votes: Record<string, 'helpful' | 'not_helpful'> = {};
      await Promise.all(
        reviews.map(async (review) => {
          const vote = await ReviewService.getUserVote(review.id, user.id);
          if (vote) {
            votes[review.id] = vote.vote_type as 'helpful' | 'not_helpful';
          }
        })
      );
      setUserVotes(votes);
    } catch (error) {
      console.error('Error loading user votes:', error);
    }
  };

  const handleVote = async (reviewId: string, voteType: 'helpful' | 'not_helpful' | null) => {
    if (!user) return;

    try {
      if (voteType) {
        await ReviewService.voteOnReview(reviewId, user.id, voteType);
        setUserVotes(prev => ({ ...prev, [reviewId]: voteType }));
      } else {
        await ReviewService.removeVote(reviewId, user.id);
        setUserVotes(prev => {
          const updated = { ...prev };
          delete updated[reviewId];
          return updated;
        });
      }
      
      // Reload reviews to get updated vote counts
      loadData();
    } catch (error) {
      console.error('Error voting on review:', error);
      toast.error('Failed to vote on review');
    }
  };

  const handleFlag = async (reviewId: string) => {
    if (!user) return;

    try {
      await ReviewService.flagReview(reviewId, user.id, 'other', 'User reported');
      toast.success('Review reported for moderation');
    } catch (error) {
      console.error('Error flagging review:', error);
      toast.error('Failed to report review');
    }
  };

  if (loading) {
    return (
      <div className={`space-y-4 ${className}`}>
        <div className="animate-pulse">
          <div className="h-32 bg-gray-200 rounded-lg mb-4"></div>
          {[1, 2, 3].map(i => (
            <div key={i} className="h-24 bg-gray-200 rounded-lg mb-4"></div>
          ))}
        </div>
      </div>
    );
  }

  // Show error state if there's an error
  if (error) {
    return (
      <div className={`space-y-6 ${className}`}>
        <Card>
          <CardContent className="pt-6 text-center py-12">
            <div className="text-red-600 mb-4">
              <Flag className="w-12 h-12 mx-auto mb-2" />
              <h4 className="font-medium mb-2">Unable to Load Reviews</h4>
              <p className="text-sm text-gray-600 mb-4">{error}</p>
              <Button onClick={loadData} variant="outline" size="sm">
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Rating Statistics */}
      {stats && <RatingStatsDisplay stats={stats} />}

      {/* Reviews Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">
            Reviews {stats && `(${stats.rating_count})`}
          </h3>
          
          {reviews.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Filter className="w-4 h-4 mr-2" />
                  Sort
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => { setSortBy('created_at'); setSortOrder('desc'); }}>
                  Newest First
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { setSortBy('created_at'); setSortOrder('asc'); }}>
                  Oldest First
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { setSortBy('rating'); setSortOrder('desc'); }}>
                  Highest Rated
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { setSortBy('rating'); setSortOrder('asc'); }}>
                  Lowest Rated
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { setSortBy('helpful_votes'); setSortOrder('desc'); }}>
                  Most Helpful
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Reviews List */}
        {reviews.length > 0 ? (
          <div>
            {reviews.map((review) => (
              <ReviewCard
                key={review.id}
                review={review}
                onVote={handleVote}
                onFlag={handleFlag}
                userVote={userVotes[review.id] || null}
              />
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="pt-6 text-center py-12">
              <Star className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <h4 className="font-medium mb-2">No reviews yet</h4>
              <p className="text-gray-600">Be the first to share your experience!</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ReviewDisplay; 